import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { Endpoint } from '@/Config/api/endpoint';
import { ConversationApi } from '@/Domain/api/Conversation/Conversation.api';
import {
  WhyAISaidThatDto,
  WhyAISaidThatScheduledDto,
} from '@/Domain/api/Conversation/dto/WhyAISaidThat.dto';

const isScheduledResponse = (
  response: WhyAISaidThatDto | WhyAISaidThatScheduledDto,
): response is WhyAISaidThatScheduledDto => 'status' in response && response.status === 'scheduled';

const AUTO_REFETCH_INTERVAL_MS = 10000;

export const useWhyAISaidThat = (
  conversationId: number | string,
  messageId: number | string | undefined | null,
) => {
  const queryClient = useQueryClient();
  const [isScheduled, setIsScheduled] = useState(false);

  const whyAISaidThatQuery = useQuery<WhyAISaidThatDto | null>({
    queryKey: [Endpoint.conversation.whyAISaidThat, conversationId, messageId],
    queryFn: () => ConversationApi.getWhyAISaidThat(String(conversationId), String(messageId)),
    enabled: !!conversationId && !!messageId,
    retry: 0,
    staleTime: 0,
    refetchOnMount: 'always',
    // Auto-refetch every 10 seconds when scheduled and data not yet available
    refetchInterval: isScheduled ? AUTO_REFETCH_INTERVAL_MS : false,
  });

  // Stop auto-refetching once data is received
  if (isScheduled && whyAISaidThatQuery.data) {
    setIsScheduled(false);
  }

  const scheduleMutation = useMutation({
    mutationFn: () =>
      ConversationApi.scheduleWhyAISaidThat(String(conversationId), String(messageId)),
    onMutate: () => {
      // Set scheduled immediately to prevent retry loops if the request fails
      setIsScheduled(true);
    },
    onSuccess: (response) => {
      if (!isScheduledResponse(response)) {
        // If we got the data back immediately, update the cache and clear scheduled state
        queryClient.setQueryData(
          [Endpoint.conversation.whyAISaidThat, conversationId, messageId],
          response,
        );
        setIsScheduled(false);
      }
    },
  });

  const resetScheduled = () => setIsScheduled(false);

  return {
    whyAISaidThat: whyAISaidThatQuery.data,
    isLoading: whyAISaidThatQuery.isLoading,
    isFetched: whyAISaidThatQuery.isFetched,
    isRefetching: whyAISaidThatQuery.isRefetching,
    isScheduled,
    isScheduling: scheduleMutation.isPending,
    scheduleGeneration: scheduleMutation.mutate,
    resetScheduled,
  };
};
